import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { API_BASE } from '../config';
import { useTheme } from '../context/ThemeContext';
import { getPlatformCode } from '../platform';
import Button from '../components/Button';
import Icon from '../components/Icon';
import { EmptyState } from '../components/PlayerUI';

interface Game { id: string; name: string; imageUrl: string | null }

export default function GuestLanding() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { colors } = useTheme();
  const [games, setGames] = useState<Game[]>([]);
  const [search, setSearch] = useState('');

  useEffect(() => {
    const platformCode = getPlatformCode();
    const url = platformCode ? `${API_BASE}/games?platformCode=${encodeURIComponent(platformCode)}` : `${API_BASE}/games`;
    axios.get<Game[]>(url).then(response => setGames(response.data)).catch(() => setGames([]));
  }, []);

  const visibleGames = useMemo(() => games.filter(game => game.name.toLowerCase().includes(search.trim().toLowerCase())), [games, search]);

  return (
    <main className="sls-guest-landing sls-page">
      <div className="sls-page-content" style={{ paddingTop: 'calc(24px + env(safe-area-inset-top, 0px))' }}>
        <img src="/icons/icon-192.png" alt="SL Sweep" style={{ width: 56, height: 56 }} />
        <h1 style={{ margin: '20px 0 6px', fontSize: 28, fontWeight: 700 }}>{t('auth.preview.exploreTitle')}</h1>
        <p style={{ margin: '0 0 20px', fontSize: 16, lineHeight: 1.5 }}>{t('auth.preview.exploreBody')}</p>
        <div style={{ display: 'flex', gap: 10, marginBottom: 20 }}>
          <Button label={t('auth.preview.createAccount')} onClick={() => navigate('/register')} />
          <Button label={t('auth.preview.signInShort')} variant="secondary" onClick={() => navigate('/login')} />
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 24 }}>
          <button onClick={() => navigate('/wallet-topup')} style={{ minHeight: 64, padding: 12, border: `1px solid ${colors.line}`, borderRadius: 12, background: colors.surface, color: colors.text, fontSize: 16, fontWeight: 600 }}>{t('nav.addFunds')}</button>
          <button onClick={() => navigate('/withdraw')} style={{ minHeight: 64, padding: 12, border: `1px solid ${colors.line}`, borderRadius: 12, background: colors.surface, color: colors.text, fontSize: 16, fontWeight: 600 }}>{t('nav.cashOut')}</button>
        </div>
        <h2 style={{ margin: '0 0 12px', fontSize: 20, fontWeight: 700 }}>{t('auth.preview.gamesHeader')}</h2>
        <label style={{ minHeight: 56, display: 'flex', alignItems: 'center', gap: 10, padding: '0 14px', borderRadius: 12, background: colors.line }}>
          <Icon name="magnifying-glass" size={17} color={colors.textMuted} />
          <input aria-label={t('auth.preview.search')} value={search} onChange={event => setSearch(event.target.value)} placeholder={t('auth.preview.search')} style={{ flex: 1, border: 0, outline: 0, background: 'transparent', color: colors.text, fontSize: 16 }} />
        </label>

        {visibleGames.length ? (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginTop: 24 }}>
            {visibleGames.map((game, index) => (
              <div key={game.id} style={{ display: 'contents' }}>
                {index === 4 ? (
                  <section style={{ gridColumn: '1 / -1', padding: 20, borderRadius: 14, background: colors.line }}>
                    <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700 }}>{t('auth.preview.inlineTitle')}</h2>
                    <Button label={t('auth.preview.createAccount')} onClick={() => navigate('/register')} style={{ marginTop: 16 }} />
                    <Button label={t('auth.preview.signIn')} variant="text" onClick={() => navigate('/login')} style={{ marginTop: 6 }} />
                  </section>
                ) : null}
                <article style={{ minHeight: 220, padding: 14, borderRadius: 14, border: `1px solid ${colors.line}`, display: 'flex', flexDirection: 'column' }}>
                  <div style={{ aspectRatio: '1', borderRadius: 12, background: colors.line, display: 'grid', placeItems: 'center', overflow: 'hidden' }}>
                    {game.imageUrl ? <img src={game.imageUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'contain' }} /> : <Icon name="gamepad" size={28} color={colors.text} />}
                  </div>
                  <h2 style={{ margin: '12px 0 4px', fontSize: 17, fontWeight: 600 }}>{game.name}</h2>
                  <button onClick={() => navigate('/games')} style={{ marginTop: 'auto', minHeight: 48, padding: 0, border: 0, background: 'transparent', color: colors.accent, fontSize: 15, fontWeight: 600, textAlign: 'left' }}>{t('auth.preview.viewGame')}</button>
                </article>
              </div>
            ))}
          </div>
        ) : <EmptyState icon="gamepad" title={t('auth.preview.noGames')} body={t('auth.preview.noGamesBody')} action={search ? { label: t('auth.preview.clearSearch'), onPress: () => setSearch('') } : { label: t('auth.preview.learnMore'), onPress: () => navigate('/help') }} />}
      </div>
    </main>
  );
}
